// ─── Results hero summary (Provider-Only Marketplace Hotfix V1, Part A) ────
// Pure, directly-testable derivation of ResultsHero's headline/counts/
// explanation copy — extracted out of the component so the priority logic
// (backend search depth vs. shallow sequence counts vs. genuinely-loading)
// can be unit-tested with real assertions instead of grepping JSX. This is
// exactly the logic that produced the Madrid bug: ResultsHero used to read
// ONLY the sequence-derived numbers, so once /experiences/search loaded 17
// real providers, the hero kept showing the sequence's shallow "3".

export interface ResultsHeroSummaryInput {
  totalActivities:        number;
  sequenceProviderCount:  number;
  sequenceLocalCount:     number;
  destinationLabel:       string;
  hasSelection:            boolean;
  selectedCount?:          number;
  selectedProviderCount?:  number;
  selectedLocalCount?:     number;
  /** Multi-city sequences never call /experiences/search — the sequence-
   *  derived numbers are the only (and correct) source for them. */
  isMultiCity:             boolean;
  searchReady:             boolean;
  searchUsingFallback:     boolean;
  searchDestinationDepth:  { provider_count: number; local_count: number } | null;
  searchFacets:            { total_provider_count: number; total_local_count: number } | null;
  /** VITE_RHEA_SHOW_LOCAL_REFERENCES — absent/false means launch mode. */
  showLocalReferences:     boolean;
}

export interface ResultsHeroSummary {
  headline:          string;
  countsLine:        string | null;
  explanationLine:   string | null;
  /** True while the first /experiences/search request for this sequence is
   *  still in flight — neither ready nor fallen back yet. The caller must
   *  never show a numeric claim in this state; headline is already a
   *  neutral loading sentence instead. */
  showLoadingState:  boolean;
}

export function deriveResultsHeroSummary(input: ResultsHeroSummaryInput): ResultsHeroSummary {
  const {
    totalActivities, sequenceProviderCount, sequenceLocalCount, destinationLabel,
    hasSelection, selectedCount, selectedProviderCount, selectedLocalCount,
    isMultiCity, searchReady, searchUsingFallback,
    searchDestinationDepth, searchFacets, showLocalReferences,
  } = input;

  const usingBackendSearch = !isMultiCity && searchReady && !searchUsingFallback;
  const isSearchLoading    = !isMultiCity && !searchReady && !searchUsingFallback;
  const showLoadingState   = isSearchLoading && !hasSelection;

  const searchProviderCount = searchDestinationDepth?.provider_count
    ?? searchFacets?.total_provider_count
    ?? null;
  const searchLocalCount = searchDestinationDepth?.local_count
    ?? searchFacets?.total_local_count
    ?? 0;
  const searchDepthTotal = searchProviderCount != null
    ? searchProviderCount + (showLocalReferences ? searchLocalCount : 0)
    : null;

  const providerCount = usingBackendSearch && searchProviderCount != null ? searchProviderCount : sequenceProviderCount;
  const localCount    = usingBackendSearch && searchProviderCount != null ? searchLocalCount : sequenceLocalCount;

  let displayCount         = usingBackendSearch && searchDepthTotal != null ? searchDepthTotal : totalActivities;
  let displayProviderCount = providerCount;
  let displayLocalCount    = localCount;

  if (hasSelection) {
    displayCount         = selectedCount ?? 0;
    displayProviderCount = selectedProviderCount ?? 0;
    displayLocalCount    = selectedLocalCount ?? 0;
  }

  const headline = showLoadingState
    ? `Buscando experiencias reservables en ${destinationLabel}…`
    : displayCount === 0
      ? "Sin experiencias disponibles para esta búsqueda."
      : hasSelection
        ? `${displayCount} en comparación`
        : `${displayCount} ${displayCount === 1 ? "experiencia" : "experiencias"} en ${destinationLabel}`;

  const countsLine = (() => {
    if (showLoadingState || displayCount === 0) return null;
    const parts: string[] = [];
    if (displayProviderCount > 0) parts.push(`${displayProviderCount} con proveedor`);
    // Hard rule (Part A #4 / Part B #5): local-reference copy never renders
    // in launch mode, regardless of what displayLocalCount computes to.
    if (showLocalReferences && displayLocalCount > 0) parts.push(`${displayLocalCount} referencias locales`);
    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  const explanationLine = showLoadingState || displayCount === 0
    ? null
    : hasSelection
      ? "Compara opciones por precio, duración y proveedor."
      : showLocalReferences
        ? "Explora experiencias disponibles y referencias locales, y añade hasta 3 para comparar."
        : "Explora experiencias reservables y compara hasta 3 opciones antes de abrir el proveedor.";

  return { headline, countsLine, explanationLine, showLoadingState };
}
